import React, { useContext, useEffect, useState } from 'react';

import {
  AssignmentOutlined,
  Close,
  DeleteOutline,
  FavoriteOutlined,
  FavoriteBorderOutlined,
  Star,
  StarOutline
} from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Modal,
  Select,
  Typography
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';

import { response } from '../../../api/db';
import { updateEvaluation } from '../../../api/db/evaluation';
import { addFavorite, deleteFavorite } from '../../../api/db/favorite';
import {
  getMylistInfo,
  deleteMylistProduct,
  deleteMylistAll
} from '../../../api/db/mylist';
import {
  alertButtonOutlinedColor,
  gray10,
  gray100,
  gray20,
  gray70,
  gray80,
  gray90,
  pink,
  primary10,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primaryColor,
  tertiaryButtonOutlinedColor,
  yellow
} from '../../../constants/Colors';
import { sideBarWidth } from '../../../constants/Location';
import {
  certificationFlgOptions,
  co2CalcBasisOptions,
  productMethodList
} from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';
import { common } from '../../../modules/error';
import { MylistGetProductType } from '../../../validation/mylist/mylistType';
import CustomHead from '../CustomHead';
import Encoding from 'encoding-japanese';
import dayjs from 'dayjs';
import { sentryLog } from '../../../config/sentry';

const styles = {
  wrapper: {
    p: '32px',
    '@media screen and (max-width:1072px)': {
      p: '24px 16px',
      mb: '80px'
    }
  },
  title: {
    fontSize: '32px',
    fontWeight: 500,
    color: gray100,
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    }
  },
  downloadButton: {
    ml: '17px',
    mr: '17px',
    height: '44px',
    width: '94px',
    fontSize: '16px',
    fontWeight: 500,
    ...primaryButtonBackgroundColor
  },
  deleteButton: {
    height: '44px',
    width: '100px',
    fontSize: '16px',
    ...alertButtonOutlinedColor,
    '@media screen and (max-width:600px)': {
      height: '32px',
      fontSize: '14px',
      width: '88px'
    }
  },
  searchButton: {
    fontSize: '16px',
    width: '560px',
    height: '43px',
    ...primaryButtonOutlinedColor,
    '@media screen and (max-width:600px)': {
      width: '100%',
      mb: '122px'
    }
  },
  displayNoneOnMobile: {
    '@media screen and (max-width:1071px)': {
      display: 'none'
    }
  },
  displayNoneOnDesktop: {
    '@media screen and (min-width:1072px)': {
      display: 'none'
    }
  },
  icon: {
    width: '80px',
    height: '80px',
    pb: '16px',
    '@media screen and (max-width:600px)': {
      width: '64px',
      height: '64px'
    }
  },
  spButtonContainer: {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    zIndex: 1,
    '@media screen and (min-width:1072px)': {
      display: 'none'
    },
    '@media screen and (min-width:961px)': {
      width: `calc(100% - ${sideBarWidth})`,
      left: sideBarWidth
    }
  },
  downloadButtonSp: {
    ...primaryButtonBackgroundColor,
    borderRadius: '4px',
    fontSize: '16px',
    width: '95%',
    height: '44px'
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxWidth: '512px',
    minWidth: '294px',
    bgcolor: 'white',
    p: '24px'
  },
  mylistTitle: {
    fontSize: '24px',
    fontWeight: 500,
    color: gray100,
    '@media screen and (max-width:600px)': {
      fontSize: '18px'
    }
  },
  remarksTitle: {
    fontSize: '16px',
    fontWeight: 500,
    color: gray90,
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    }
  },
  remarks: {
    fontSize: '16px',
    color: gray100,
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    }
  },
  backButton: {
    color: primaryColor,
    mt: '24px'
  }
};

const MyListProduct = (): React.ReactElement => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [openAllDeleteModal, setOpenAllDeleteModal] = useState<boolean>(false);
  const { isLoggedIn, userId, userRank, userType } = useContext(AuthContext);
  const [dataTotalCount, setDataTotalCount] = useState<number>(0);
  const [displayData, setDisplayData] = useState<
    Map<string, MylistGetProductType[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarErrorText, setSnackbarErrorText] = useState<string>('');
  const [snackbarInfoText, setSnackbarInfoText] = useState<string>('');

  // CSVの作成、ダウンロード
  const csvHeader = [
    '大分類',
    '部材名',
    '一般名称',
    '商品名',
    '仕様',
    '概要説明',
    '適用条件',
    '画像1URL',
    '画像2URL',
    '画像3URL',
    '説明資料ファイルURL',
    '基準CO2算定仕様',
    '計算したCO2排出原単位',
    '算定したCO2排出原単位',
    'CO2削減率',
    'CO2計算補足',
    'CO2算定根拠 種別',
    'CO2算定根拠 名称',
    'CO2算定根拠 ファイルURL',
    'CO2算定根拠 URL',
    'その他の認定情報',
    '参考設計単価',
    '単価設定日',
    '単価補足説明',
    '関連URL',
    'その他備考',
    '会社名',
    '担当者',
    'メールアドレス',
    'TEL',
    '企業の環境認定',
    '認定の名称'
  ];

  const escapeCsvField = (field: string | number): string => {
    const fieldStr = String(field);
    if (fieldStr.includes(',') || fieldStr.includes('\n')) {
      return `"${fieldStr.replace(/"/g, '""')}"`;
    }
    return fieldStr;
  };

  const generateCsvDataRow = (product: MylistGetProductType): string => {
    const basisTypeToString = (type: number): string => {
      const foundOption = co2CalcBasisOptions.find(
        (option) => option.value === type
      );
      return foundOption ? foundOption.label : '';
    };

    const dataForCsv = [
      product.category.name || '',
      product.material.name || '',
      product.alias || '',
      product.name || '',
      product.specification || '',
      product.summary || '',
      product.condition || '',
      product.image[0]?.filepath || '',
      product.image[1]?.filepath || '',
      product.image[2]?.filepath || '',
      product.document.path || '',
      product.material.calculation_specification || '',
      product.calculated_emission_intensity || '',
      product.emission_intensity?.toString() || '',
      product.reduce_rate?.toString() || '',
      product.calculation_supplement || '',
      basisTypeToString(product.calculation_basis_type) || '',
      product.calculation_basis_name || '',
      product.calculation_basis_file.path || '',
      product.calculation_basis_url || '',
      product.other_certification || '',
      product.reference_unit_price?.toString() || '',
      product.setting_at
        ? dayjs(product.setting_at).format('YYYY年MM月DD日')
        : '',
      product.price_remarks || '',
      product.related_url || '',
      product.remark || '',
      product.users.company || '',
      product.users.last_name + product.users.first_name || '',
      product.users.email || '',
      product.users.tel || '',
      product.users?.certification_flg === certificationFlgOptions[1].value
        ? '取得済'
        : product.users?.certification_flg === certificationFlgOptions[0].value
        ? '未取得'
        : '',
      product.users.certification_name || ''
    ];
    return dataForCsv.map(escapeCsvField).join(',');
  };

  const handleDownloadCsv = (): void => {
    const newCsvData = dataListForCsv.join('\n');
    const unicodeArray = Encoding.stringToCode(newCsvData);
    const sjisArray = Encoding.convert(unicodeArray, {
      from: 'UNICODE',
      to: 'SJIS'
    });
    const blob = new Blob([new Uint8Array(sjisArray)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const now = dayjs();
    const formattedDate = now.format('YYYYMMDD');
    a.href = url;
    a.download = `${mylistData.title}_${formattedDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    window.dataLayer.push({
      event: 'mylist_download',
      screen: window.location.pathname,
      category: 'button',
      action: 'mylist_mylist_download',
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });
  };

  const [dataListForCsv, setDataListForCsv] = useState<string[]>([
    csvHeader.join(',')
  ]);

  // 計算方法
  const [selectedMethod, setSelectedMethod] = useState(
    Number(localStorage.getItem('selectedMethod')) || 1
  );
  const handleChangeMethod = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as number;
    setSelectedMethod(value);
    localStorage.setItem('selectedMethod', String(value));
    setDataListForCsv([]);
    window.dataLayer.push({
      event: 'co2_method',
      screen: window.location.pathname,
      category: 'pulldown',
      action: 'mylist_co2_method',
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });
  };

  const [mylistData, setMylistData] = useState<{
    title: string;
    remarksFirst: string | null;
    remarksSecond: string | null;
  }>({
    title: '',
    remarksFirst: null,
    remarksSecond: null
  });

  // マイリストを取得する
  const getList = async () => {
    const offset = 0;
    const order = 1;

    const res = await getMylistInfo(Number(id), offset, order, selectedMethod);
    setDataTotalCount(res.response_count);

    setMylistData({
      title: res.title,
      remarksFirst: res.remarks_first,
      remarksSecond: res.remarks_second
    });

    const dataList = new Map<string, MylistGetProductType[]>();
    const dataForCsv = [csvHeader.join(',')];
    res.products.forEach((product) => {
      if (dataList.has(product.category.name)) {
        dataList.get(product.category.name)?.push(product);
      } else {
        dataList.set(product.category.name, [product]);
      }
      const data = generateCsvDataRow(product);
      dataForCsv.push(data);
    });
    setDataListForCsv(dataForCsv);
    setDisplayData(dataList);
  };

  // 計算方法を変更した時、データを取得する
  useEffect((): void => {
    // ログインチェック
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    }

    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        await getList();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const responseError = axiosError.response as response;
          setSnackbarErrorText(responseError.data.message);
        } else {
          setSnackbarErrorText(common);
        }
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isLoggedIn, selectedMethod]);

  // 一括削除
  const handleDeleteAllMyList = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const dataArray = Array.from(displayData.values())
        .map((value) => value.map((product) => ({ id: product.id })))
        .flat();
      await deleteMylistAll(dataArray, Number(id));
      await getList();
      setSnackbarInfoText('マイリストから削除しました');
      window.dataLayer.push({
        event: 'click',
        screen: window.location.pathname,
        category: 'button',
        action: 'mylist_all_delete',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const responseError = axiosError.response as response;
        setSnackbarErrorText(responseError.data.message);
      } else {
        setSnackbarErrorText(common);
      }
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
      setOpenAllDeleteModal(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <CustomHead title={`マイリスト ${mylistData.title} | CMCOS[コムコス]`} />
      <Grid>
        <CustomLoading loading={isLoading} />
        <SideBar />
        <Grid
          sx={{
            '@media screen and (min-width:961px)': {
              ml: sideBarWidth
            }
          }}
        >
          <Grid
            sx={styles.spButtonContainer}
            container
            justifyContent="center"
            alignItems="center"
          >
            <Button
              sx={styles.downloadButtonSp}
              disabled={dataTotalCount === 0}
              onClick={handleDownloadCsv}
            >
              CSV出力
            </Button>
          </Grid>
          <Box sx={styles.wrapper}>
            <Grid
              container
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>
                <Typography sx={styles.title}>マイリスト</Typography>
              </Grid>
              <Grid item>
                <Grid
                  container
                  flexDirection="row"
                  justifyContent="center"
                  sx={[{ height: '44px' }, styles.displayNoneOnMobile]}
                >
                  <CustomSelectField
                    list={productMethodList}
                    onChange={handleChangeMethod}
                    value={selectedMethod}
                    width="200px"
                    height="44px"
                    type="primary"
                  />
                  <Button
                    sx={styles.downloadButton}
                    disabled={dataTotalCount === 0}
                    onClick={handleDownloadCsv}
                  >
                    CSV出力
                  </Button>
                  <Button
                    sx={styles.deleteButton}
                    disabled={dataTotalCount === 0}
                    onClick={() => setOpenAllDeleteModal(true)}
                  >
                    一括削除
                  </Button>
                </Grid>
                <Button
                  sx={[styles.deleteButton, styles.displayNoneOnDesktop]}
                  disabled={dataTotalCount === 0}
                  onClick={() => setOpenAllDeleteModal(true)}
                >
                  一括削除
                </Button>
              </Grid>
            </Grid>
            <Grid mt={'24px'} sx={styles.mylistTitle}>
              {mylistData.title}
            </Grid>
            <Divider sx={{ mt: '16px', mb: '9px', color: gray70 }} />
            {mylistData.remarksFirst && (
              <>
                <Grid sx={styles.mylistTitle}>
                  <Typography sx={styles.remarksTitle}>備考１</Typography>
                  <Typography mt={'9px'} sx={styles.remarks}>
                    {mylistData.remarksFirst}
                  </Typography>
                </Grid>
              </>
            )}
            {mylistData.remarksSecond && (
              <>
                <Divider sx={{ mt: '16px', mb: '9px', color: gray70 }} />
                <Grid sx={styles.mylistTitle}>
                  <Typography sx={styles.remarksTitle}>備考２</Typography>
                  <Typography mt={'9px'} sx={styles.remarks}>
                    {mylistData.remarksSecond}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid sx={[styles.displayNoneOnDesktop, { mt: '26px' }]}>
              <CustomSelectField
                list={productMethodList}
                onChange={handleChangeMethod}
                value={selectedMethod}
                width="100%"
                height="44px"
                type="primary"
              />
            </Grid>
            {dataTotalCount === 0 && (
              <Grid
                container
                flexDirection="column"
                alignItems="center"
                sx={{
                  mt: '96px',
                  '@media screen and (max-width:600px)': {
                    mt: '54px',
                    width: '100%'
                  }
                }}
              >
                <AssignmentOutlined sx={styles.icon} />
                <Typography
                  sx={{
                    color: gray100,
                    fontSize: '24px',
                    fontWeight: 500,
                    pb: '24px'
                  }}
                  textAlign="center"
                >
                  マイリストに登録した建材はまだありません
                </Typography>
                <Typography
                  sx={{ color: gray100, fontSize: '16px', pb: '80px' }}
                  textAlign="center"
                >
                  建材検索で建材をマイリストに登録するとここに表示されます
                </Typography>
                <Button
                  sx={styles.searchButton}
                  variant="outlined"
                  onClick={() => navigate('/material')}
                >
                  建材を検索する
                </Button>
              </Grid>
            )}
            {dataTotalCount > 0 && (
              <>
                <Grid
                  sx={{
                    mt: '22px',
                    border: '1px solid' + gray70,
                    borderBottom: 'none',
                    overflowX: 'auto',
                    width: '100%',
                    maxWidth: '1184px',
                    '@media screen and (min-width:1488px)': {
                      maxWidth: '100%'
                    }
                  }}
                >
                  <Grid
                    container
                    flexDirection="row"
                    sx={{
                      height: '32px',
                      minWidth: '1184px',
                      backgroundColor: primary10,
                      flexWrap: 'nowrap',
                      borderBottom: '1px solid' + gray70,
                      '@media screen and (min-width:1488px)': {
                        minWidth: '100%'
                      }
                    }}
                  >
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '120px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '10%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        部材名
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '144px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '12%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        一般名称
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '176px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '15%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        商品名
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '120px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '10%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        仕様
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '120px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '10%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        CO2原単位
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '88px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '8%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        CO2削減率
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        width: '120px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        '@media screen and (min-width:1488px)': {
                          width: '10%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        会社名
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid' + gray70,
                        height: '32px',
                        pl: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        width: '176px',
                        '@media screen and (min-width:1488px)': {
                          width: '15%'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: gray100 }}>
                        今回のあなたの評価
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        width: '120px',
                        '@media screen and (min-width:1488px)': {
                          width: '10%'
                        }
                      }}
                    ></Grid>
                  </Grid>
                  {Array.from(displayData.entries()).map(
                    ([key, value], index) => {
                      return (
                        <>
                          <Grid
                            sx={{
                              minWidth: '1184px',
                              height: '26px',
                              borderBottom: '1px solid' + gray70,
                              backgroundColor: gray20
                            }}
                            container
                            alignItems="center"
                            key={index}
                          >
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: gray100,
                                pl: '12px'
                              }}
                            >
                              {key}
                            </Typography>
                          </Grid>
                          {value.map((rowData, index) => (
                            <TableRow
                              key={index}
                              rowData={rowData}
                              getList={getList}
                              setIsLoading={setIsLoading}
                              setOpenSnackbar={setOpenSnackbar}
                              setSnackbarErrorText={setSnackbarErrorText}
                              setSnackbarInfoText={setSnackbarInfoText}
                              mylistId={Number(id)}
                            />
                          ))}
                        </>
                      );
                    }
                  )}
                </Grid>
                <Button
                  sx={styles.backButton}
                  onClick={() => navigate(`/mylist`)}
                >
                  <ChevronLeftIcon sx={{ mr: '8px' }} />
                  戻る
                </Button>
              </>
            )}
          </Box>
        </Grid>
        <Modal
          open={openAllDeleteModal}
          onClose={() => setOpenAllDeleteModal(false)}
          sx={{ m: '16px' }}
        >
          <Box sx={styles.modal}>
            <Box
              sx={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                width: '24px',
                height: '24px',
                color: gray90
              }}
            >
              <Close
                onClick={() => setOpenAllDeleteModal(false)}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                color: gray100,
                mb: '24px'
              }}
            >
              マイリストから一括削除
            </Typography>
            <Typography sx={{ fontSize: '16px', color: gray100, mb: '40px' }}>
              削除すると元に戻すことはできません。よろしいですか？
            </Typography>
            <Grid
              container
              sx={{ width: '100%' }}
              justifyContent="space-between"
            >
              <Button
                variant="outlined"
                sx={{
                  ...tertiaryButtonOutlinedColor,
                  fontSize: '16px',
                  height: '43px',
                  width: '50%',
                  maxWidth: '248px',
                  '@media screen and (max-width:600px)': {
                    maxWidth: '140px'
                  }
                }}
                onClick={() => setOpenAllDeleteModal(false)}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                sx={{
                  ...primaryButtonBackgroundColor,
                  fontSize: '16px',
                  height: '43px',
                  width: '50%',
                  maxWidth: '248px',
                  '@media screen and (max-width:600px)': {
                    maxWidth: '140px'
                  }
                }}
                onClick={handleDeleteAllMyList}
              >
                削除する
              </Button>
            </Grid>
          </Box>
        </Modal>
        <CustomSnackbar
          open={openSnackbar}
          text={snackbarErrorText}
          infoText={snackbarInfoText}
          onClose={() => setOpenSnackbar(false)}
        />
      </Grid>
    </>
  );
};

export default MyListProduct;

const TableRow = (props: {
  rowData: MylistGetProductType;
  getList: () => void;
  setIsLoading: (value: boolean) => void;
  setOpenSnackbar: (value: boolean) => void;
  setSnackbarErrorText: (value: string) => void;
  setSnackbarInfoText: (value: string) => void;
  mylistId: number;
}): React.ReactElement => {
  const {
    rowData,
    getList,
    setIsLoading,
    setOpenSnackbar,
    setSnackbarErrorText,
    setSnackbarInfoText,
    mylistId
  } = props;
  const navigate = useNavigate();
  const { userId, userType, userRank } = useContext(AuthContext);
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxWidth: '512px',
    minWidth: '294px',
    bgcolor: 'white',
    p: '24px'
  };

  const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);

  const handleOpenDeleteModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    setOpenDeleteModal(true);
    e.stopPropagation();
  };

  // 建材1件をマイリストから削除
  const handleDeleteMyList = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await deleteMylistProduct(rowData.id, Number(mylistId));
      await getList();
      setSnackbarInfoText(`マイリスト「${res.data.title}」から削除しました`);
      window.dataLayer.push({
        event: 'mylist_del',
        screen: window.location.pathname,
        category: 'button',
        action: 'mylist_mylist_del',
        label: rowData.name,
        value: rowData.id,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const responseError = axiosError.response as response;
        setSnackbarErrorText(responseError.data.message);
      } else {
        setSnackbarErrorText(common);
      }
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
      setOpenDeleteModal(false);
      setOpenSnackbar(true);
    }
  };

  // お気に入りボタン押下時
  const toggleFavorite = async () => {
    setIsLoading(true);
    try {
      if (rowData.favorite_flg) {
        await deleteFavorite(rowData.id);
        setSnackbarInfoText('お気に入りから削除しました');
        window.dataLayer.push({
          event: 'favorite_del',
          screen: window.location.pathname,
          category: 'button',
          action: 'mylist_favorite_del',
          label: rowData.name,
          value: rowData.id,
          user_id: userId,
          user_type: userType,
          user_rank: userRank
        });
      } else {
        await addFavorite(rowData.id);
        setSnackbarInfoText('お気に入りに追加しました');
        window.dataLayer.push({
          event: 'favorite',
          screen: window.location.pathname,
          category: 'button',
          action: 'mylist_favorite',
          label: rowData.name,
          value: rowData.id,
          user_id: userId,
          user_type: userType,
          user_rank: userRank
        });
      }
      await getList();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const responseError = axiosError.response as response;
        setSnackbarErrorText(responseError.data.message);
      } else {
        setSnackbarErrorText(common);
      }
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  // スコアの更新
  const updateScore = async (
    event: SelectChangeEvent<unknown>
  ): Promise<void> => {
    setIsLoading(true);
    try {
      // 評価更新API呼び出し
      const selectedScore = event.target.value as number;

      await updateEvaluation(rowData.evaluations.id, selectedScore);
      await getList();

      window.dataLayer.push({
        event: 'score',
        screen: window.location.pathname,
        category: 'pulldown',
        action: 'mylist_score',
        label: rowData.name,
        value: rowData.id,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setSnackbarErrorText(loginErrorResponse.data.message);
        setOpenSnackbar(true);
      } else {
        setSnackbarErrorText(common);
        setOpenSnackbar(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const scoreOptions = [
    { label: '3.0', value: 3 },
    { label: '4.0', value: 4 },
    { label: '5.0', value: 5 }
  ];

  return (
    <Grid
      container
      flexDirection="row"
      sx={{
        borderBottom: '1px solid' + gray70,
        maxHeight: '44px',
        minWidth: '1184px',
        '&:hover': {
          bgcolor: gray10,
          cursor: 'pointer'
        },
        '@media screen and (min-width:1488px)': {
          minWidth: '100%'
        }
      }}
    >
      <Grid container item>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '120px',
            height: '44px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            '@media screen and (min-width:1488px)': {
              width: '10%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography sx={{ fontSize: '12px', color: gray100 }}>
            {rowData.material.name?.length > 7
              ? rowData.material.name.slice(0, 7) + '...'
              : rowData.material.name}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '144px',
            height: '44px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            '@media screen and (min-width:1488px)': {
              width: '12%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography sx={{ fontSize: '12px', color: gray100 }}>
            {rowData.alias?.length > 9
              ? rowData.alias.slice(0, 9) + '...'
              : rowData.alias}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '176px',
            height: '44px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            '@media screen and (min-width:1488px)': {
              width: '15%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography sx={{ fontSize: '12px', color: gray100 }}>
            {rowData.name?.length > 13
              ? rowData.name.slice(0, 13) + '...'
              : rowData.name}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '120px',
            height: '44px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            '@media screen and (min-width:1488px)': {
              width: '10%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography sx={{ fontSize: '12px', color: gray100 }}>
            {rowData.specification?.length > 8
              ? rowData.specification.slice(0, 8) + '...'
              : rowData.specification}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '120px',
            height: '44px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            '@media screen and (min-width:1488px)': {
              width: '10%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: primaryColor,
              fontWeight: 700,
              textAlign: 'center'
            }}
          >
            {rowData.calculated_emission_intensity !== null
              ? rowData.calculated_emission_intensity
              : 'ー'}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: gray100 }}>
            kg-CO2/{rowData.material.co2_unit}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '88px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@media screen and (min-width:1488px)': {
              width: '8%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography
            sx={{ fontSize: '12px', color: primaryColor, fontWeight: 700 }}
          >
            {rowData.reduce_rate ? '△' + rowData.reduce_rate : 'ー'}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: gray100, ml: '2px' }}>
            %
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            borderRight: '1px solid' + gray70,
            width: '120px',
            height: '44px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            '@media screen and (min-width:1488px)': {
              width: '10%'
            }
          }}
          onClick={() => {
            navigate('/material/' + rowData.id);
          }}
        >
          <Typography sx={{ fontSize: '12px', color: gray100 }}>
            {rowData.users.company_short}
          </Typography>
        </Grid>
        <Grid
          item
          container
          sx={{
            alignItems: 'center',
            borderRight: '1px solid' + gray70,
            width: '176px',
            height: '44px',
            pl: '12px',
            '@media screen and (min-width:1488px)': {
              width: '15%'
            }
          }}
        >
          {Array.from({ length: rowData.evaluations.score }).map((_, index) => (
            <Star
              key={index}
              sx={{ color: yellow, width: '20px', height: '20px' }}
            />
          ))}
          {Array.from({ length: 5 - rowData.evaluations.score }).map(
            (_, index) => (
              <StarOutline
                key={index}
                sx={{ color: gray80, width: '20px', height: '20px' }}
              />
            )
          )}
          <FormControl>
            <Select
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '& .MuiSelect-select': {
                  p: '0 0 0 4px'
                },
                height: '21px',
                width: '100%'
              }}
              value={rowData.evaluations.score ? rowData.evaluations.score : 0}
              onChange={updateScore}
            >
              {rowData.evaluations.score === 0 ? (
                <MenuItem
                  value={0}
                  sx={{
                    color: gray100,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  disabled
                >
                  0.0
                </MenuItem>
              ) : (
                <></>
              )}
              {scoreOptions.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{
                    color: gray100,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          sx={{
            width: '120px',
            pl: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@media screen and (min-width:1488px)': {
              width: '10%',
              pl: '0'
            }
          }}
        >
          <Button
            sx={{
              border: '1px solid' + gray80,
              maxWidth: '42px',
              minWidth: '42px',
              height: '32px',
              mr: '12px'
            }}
            onClick={toggleFavorite}
          >
            {rowData.favorite_flg ? (
              <FavoriteOutlined
                sx={{ color: pink, widht: '18px', height: '18px' }}
              />
            ) : (
              <FavoriteBorderOutlined
                sx={{ color: gray90, widht: '18px', height: '18px' }}
              />
            )}
          </Button>
          <Button
            sx={{
              border: '1px solid' + gray80,
              maxWidth: '42px',
              minWidth: '42px',
              height: '32px'
            }}
            onClick={(event) => handleOpenDeleteModal(event)}
          >
            <DeleteOutline
              sx={{ color: gray90, widht: '18px', height: '18px' }}
            />
          </Button>
        </Grid>
      </Grid>
      <Modal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        sx={{ m: '16px' }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: '24px',
              height: '24px',
              color: gray90
            }}
          >
            <Close
              onClick={() => setOpenDeleteModal(false)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 500,
              color: gray100,
              mb: '24px'
            }}
          >
            マイリストから削除
          </Typography>
          <Typography sx={{ fontSize: '16px', color: gray100, mb: '40px' }}>
            削除すると元に戻すことはできません。よろしいですか？
          </Typography>
          <Grid container sx={{ width: '100%' }} justifyContent="space-between">
            <Button
              fullWidth
              variant="outlined"
              sx={{
                ...tertiaryButtonOutlinedColor,
                fontSize: '16px',
                height: '43px',
                width: '50%',
                maxWidth: '248px',
                '@media screen and (max-width:600px)': {
                  maxWidth: '140px'
                }
              }}
              onClick={() => setOpenDeleteModal(false)}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{
                ...primaryButtonBackgroundColor,
                fontSize: '16px',
                height: '43px',
                width: '50%',
                maxWidth: '248px',
                '@media screen and (max-width:600px)': {
                  maxWidth: '140px'
                }
              }}
              onClick={handleDeleteMyList}
            >
              削除する
            </Button>
          </Grid>
        </Box>
      </Modal>
    </Grid>
  );
};
